import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";

// ─── Design System Colors ────────────────────────────────────────────────────
const COLORS = {
  bg: "#0D1117",
  card: "#161B22",
  text: "#C9D1D9",
  muted: "#8B949E",
  accent: "#58A6FF",
  green: "#238636",
  greenHover: "#2EA043",
  red: "#DA3633",
  border: "#30363D",
};

// ─── Inline Styles ───────────────────────────────────────────────────────────
const styles = {
  page: {
    minHeight: "100vh",
    background: `linear-gradient(135deg, ${COLORS.bg} 0%, ${COLORS.card} 100%)`,
    color: COLORS.text,
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif',
    padding: "100px 20px 40px",
    position: "relative",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  backBtn: {
    position: "fixed",
    top: 16,
    left: 16,
    zIndex: 1100,
    background: "rgba(255,255,255,0.08)",
    backdropFilter: "blur(12px)",
    border: `1px solid ${COLORS.border}`,
    borderRadius: 50,
    padding: "10px 22px",
    color: COLORS.accent,
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    gap: 8,
    transition: "all .3s ease",
  },
  container: {
    width: "100%",
    maxWidth: "600px",
    background: "rgba(22, 27, 34, 0.6)",
    backdropFilter: "blur(16px)",
    border: `1px solid ${COLORS.border}`,
    borderRadius: 16,
    padding: 32,
    boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.37)",
    textAlign: "center",
  },
  title: {
    fontSize: 28,
    fontWeight: 700,
    marginBottom: 8,
    background: `linear-gradient(45deg, #FFF, ${COLORS.accent})`,
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
  subtitle: {
    fontSize: 14,
    color: COLORS.muted,
    marginBottom: 24,
  },
  dropZone: {
    border: `2px dashed ${COLORS.border}`,
    borderRadius: 12,
    padding: "40px 20px",
    cursor: "pointer",
    background: "rgba(255,255,255,0.02)",
    transition: "all 0.3s ease",
    position: "relative",
  },
  dropZoneActive: {
    borderColor: COLORS.accent,
    background: "rgba(88, 166, 255, 0.05)",
  },
  fileInput: {
    display: "none",
  },
  uploadIcon: {
    fontSize: 48,
    color: COLORS.accent,
    marginBottom: 16,
  },
  fileList: {
    marginTop: 20,
    maxHeight: "260px",
    overflowY: "auto",
    textAlign: "left",
    display: "flex",
    flexDirection: "column",
    gap: 12,
    paddingRight: 6,
  },
  fileItem: {
    padding: "12px 16px",
    background: "rgba(255,255,255,0.03)",
    borderRadius: 10,
    border: `1px solid ${COLORS.border}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    transition: "all 0.2s ease",
  },
  fileItemInfo: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minWidth: 0,
  },
  fileItemName: {
    fontSize: 14,
    fontWeight: 500,
    color: COLORS.text,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  fileItemSize: {
    fontSize: 12,
    color: COLORS.muted,
    marginTop: 2,
  },
  fileItemStatus: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  statusBadge: {
    fontSize: 11,
    fontWeight: 600,
    padding: "4px 8px",
    borderRadius: 6,
    whiteSpace: "nowrap",
  },
  removeBtn: {
    background: "none",
    border: "none",
    color: COLORS.muted,
    cursor: "pointer",
    fontSize: 14,
    padding: 4,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 4,
    transition: "all 0.2s ease",
    opacity: 0.7,
  },
  button: {
    width: "100%",
    marginTop: 24,
    padding: "14px 20px",
    background: COLORS.green,
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontWeight: 600,
    fontSize: 16,
    cursor: "pointer",
    transition: "background 0.2s ease, transform 0.1s ease",
  },
  buttonDisabled: {
    background: COLORS.border,
    color: COLORS.muted,
    cursor: "not-allowed",
  },
  progressContainer: {
    marginTop: 8,
    width: "100%",
    background: "rgba(255,255,255,0.08)",
    borderRadius: 4,
    height: 6,
    overflow: "hidden",
    position: "relative",
  },
  progressBar: {
    height: "100%",
    background: `linear-gradient(90deg, ${COLORS.accent}, #00D2FF)`,
    transition: "width 0.1s ease",
    borderRadius: 4,
  },
  message: {
    marginTop: 20,
    padding: 12,
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 500,
  },
  successMsg: {
    background: "rgba(35, 134, 54, 0.15)",
    border: `1px solid ${COLORS.green}`,
    color: "#56d364",
  },
  errorMsg: {
    background: "rgba(218, 54, 51, 0.15)",
    border: `1px solid ${COLORS.red}`,
    color: "#ff7b72",
  },
  linkCard: {
    marginTop: 16,
    padding: 16,
    background: "rgba(255,255,255,0.04)",
    borderRadius: 8,
    border: `1px solid ${COLORS.border}`,
    textAlign: "left",
  },
  linkLabel: {
    fontSize: 12,
    color: COLORS.muted,
    marginBottom: 4,
  },
  linkValue: {
    fontSize: 14,
    color: COLORS.accent,
    textDecoration: "none",
    wordBreak: "break-all",
    fontWeight: 600,
    display: "inline-block",
  },
};

// ─── Configuration ──────────────────────────────────────────────────────────
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB limit
const ALLOWED_MIME_TYPES = []; // Empty allows any file type. Add types to restrict e.g., ["image/png", "application/pdf"]

export default function Upload() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [files, setFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // 1. Handle File Selection and Client-side Validations
  const validateAndAddFiles = (selectedFiles) => {
    if (!selectedFiles || selectedFiles.length === 0) return;

    const newFiles = Array.from(selectedFiles).map((selectedFile) => {
      let status = "pending";
      let errorMessage = "";

      // Check size limit
      if (selectedFile.size > MAX_FILE_SIZE) {
        status = "error";
        errorMessage = `Exceeds max size of ${MAX_FILE_SIZE / (1024 * 1024)}MB.`;
      }

      // Check MIME type limit if configured
      if (ALLOWED_MIME_TYPES.length > 0 && !ALLOWED_MIME_TYPES.includes(selectedFile.type)) {
        status = "error";
        errorMessage = "File type is not allowed.";
      }

      return {
        id: `${selectedFile.name}-${selectedFile.size}-${Date.now()}-${Math.random()}`,
        file: selectedFile,
        status: status,
        progress: 0,
        errorMessage: errorMessage,
        uploadResult: null,
      };
    });

    setFiles((prev) => [...prev, ...newFiles]);
  };

  const handleFileChange = (e) => {
    validateAndAddFiles(e.target.files);
    e.target.value = ""; // Clear file input value to allow selecting same files again
  };

  // Drag and drop handlers
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files) {
      validateAndAddFiles(e.dataTransfer.files);
    }
  };

  // 2. Perform Single File Upload using XMLHttpRequest for progress events
  const uploadSingleFile = (fileItem) => {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append("file", fileItem.file);

      const xhr = new XMLHttpRequest();
      xhr.open("POST", "/.netlify/functions/upload", true);

      // Track upload progress
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          setFiles((prev) =>
            prev.map((f) =>
              f.id === fileItem.id ? { ...f, progress: percentComplete } : f
            )
          );
        }
      };

      // Load response
      xhr.onload = () => {
        let responseBody;
        try {
          responseBody = JSON.parse(xhr.responseText);
        } catch (e) {
          responseBody = { error: "Failed to parse server response." };
        }

        if (xhr.status >= 200 && xhr.status < 300 && responseBody.success) {
          resolve(responseBody);
        } else {
          reject(new Error(responseBody.error || `Upload failed with status code ${xhr.status}`));
        }
      };

      // Handle networking errors
      xhr.onerror = () => {
        reject(new Error("Network error occurred during the upload."));
      };

      xhr.send(formData);
    });
  };

  // Sequential Upload Coordinator
  const handleUpload = async () => {
    const pendingFiles = files.filter(
      (f) => f.status === "pending" || (f.status === "error" && !f.errorMessage.includes("Exceeds max size"))
    );

    if (pendingFiles.length === 0 || isUploading) return;

    setIsUploading(true);

    for (let fileItem of pendingFiles) {
      // Set individual file state to uploading
      setFiles((prev) =>
        prev.map((f) =>
          f.id === fileItem.id ? { ...f, status: "uploading", progress: 0, errorMessage: "" } : f
        )
      );

      try {
        const result = await uploadSingleFile(fileItem);
        setFiles((prev) =>
          prev.map((f) =>
            f.id === fileItem.id ? { ...f, status: "success", progress: 100, uploadResult: result } : f
          )
        );
      } catch (err) {
        setFiles((prev) =>
          prev.map((f) =>
            f.id === fileItem.id ? { ...f, status: "error", progress: 0, errorMessage: err.message } : f
          )
        );
      }
    }

    setIsUploading(false);
  };

  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  };

  return (
    <div style={styles.page}>
      <button style={styles.backBtn} onClick={() => navigate("/")}>
        ← Back to Portfolio
      </button>

      <div style={styles.container}>
        <h1 style={styles.title}>Secure File Upload</h1>
        <p style={styles.subtitle}>
          Upload files directly and securely to my Google Drive storage.
        </p>

        {/* Drag and Drop Zone */}
        <div
          style={{
            ...styles.dropZone,
            ...(dragActive ? styles.dropZoneActive : {}),
          }}
          onDragEnter={handleDrag}
          onDragOver={handleDrag}
          onDragLeave={handleDrag}
          onDrop={handleDrop}
          onClick={() => {
            if (!isUploading) {
              fileInputRef.current.click();
            }
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            disabled={isUploading}
            style={styles.fileInput}
            onChange={handleFileChange}
          />
          <div style={styles.uploadIcon}>☁️</div>
          <p style={{ margin: "0 0 8px 0", fontSize: 16, fontWeight: 600 }}>
            {files.length > 0 ? "Add more files" : "Drag & drop your files here"}
          </p>
          <p style={{ margin: 0, fontSize: 13, color: COLORS.muted }}>
            or click to browse from your device
          </p>
        </div>

        {/* Selected Files List */}
        {files.length > 0 && (
          <div style={styles.fileList}>
            {files.map((fileItem) => {
              const isPending = fileItem.status === "pending";
              const isFileUploading = fileItem.status === "uploading";
              const isSuccess = fileItem.status === "success";
              const isError = fileItem.status === "error";

              return (
                <div key={fileItem.id} style={styles.fileItem}>
                  {/* File icon / details */}
                  <div style={styles.fileItemInfo}>
                    <span style={styles.fileItemName} title={fileItem.file.name}>
                      {fileItem.file.name}
                    </span>
                    <span style={styles.fileItemSize}>
                      {formatBytes(fileItem.file.size)}
                    </span>
                    {fileItem.errorMessage && (
                      <span style={{ fontSize: 12, color: "#ff7b72", marginTop: 4, display: "block" }}>
                        {fileItem.errorMessage}
                      </span>
                    )}
                    {/* Individual progress bar */}
                    {isFileUploading && (
                      <div style={styles.progressContainer}>
                        <div style={{ ...styles.progressBar, width: `${fileItem.progress}%` }} />
                      </div>
                    )}
                  </div>

                  {/* Status indicator / Actions */}
                  <div style={styles.fileItemStatus}>
                    {isPending && (
                      <span style={{ ...styles.statusBadge, background: "rgba(255,255,255,0.06)", color: COLORS.muted }}>
                        Ready
                      </span>
                    )}
                    {isFileUploading && (
                      <span style={{ ...styles.statusBadge, color: COLORS.accent }}>
                        {fileItem.progress}%
                      </span>
                    )}
                    {isSuccess && (
                      <span style={{ ...styles.statusBadge, background: "rgba(35, 134, 54, 0.15)", color: "#56d364" }}>
                        ✓ Done
                      </span>
                    )}
                    {isError && (
                      <span style={{ ...styles.statusBadge, background: "rgba(218, 54, 51, 0.15)", color: "#ff7b72" }}>
                        ⚠ Failed
                      </span>
                    )}

                    {/* Remove button (only if not uploading and not success) */}
                    {!isUploading && !isSuccess && (
                      <button
                        style={styles.removeBtn}
                        onClick={(e) => {
                          e.stopPropagation();
                          setFiles((prev) => prev.filter((f) => f.id !== fileItem.id));
                        }}
                        title="Remove file"
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Actions section */}
        {files.length > 0 && (
          <div style={{ display: "flex", gap: 12, marginTop: 24 }}>
            {!isUploading ? (
              <>
                {/* Check if there are uploadable files */}
                {files.some((f) => f.status === "pending" || f.status === "error") ? (
                  <button
                    style={{ ...styles.button, marginTop: 0, flex: 1 }}
                    onClick={handleUpload}
                  >
                    Upload to Google Drive ({files.filter((f) => f.status === "pending" || f.status === "error").length})
                  </button>
                ) : (
                  <div style={{ ...styles.message, ...styles.successMsg, width: "100%", marginTop: 0 }}>
                    <strong>✓ All files processed successfully!</strong>
                  </div>
                )}

                <button
                  style={{
                    ...styles.button,
                    marginTop: 0,
                    flex: files.some((f) => f.status === "pending" || f.status === "error") ? "0 0 120px" : "1",
                    background: "rgba(255,255,255,0.06)",
                    border: `1px solid ${COLORS.border}`,
                    color: COLORS.text,
                  }}
                  onClick={() => setFiles([])}
                >
                  Clear List
                </button>
              </>
            ) : (
              <button style={{ ...styles.button, ...styles.buttonDisabled, marginTop: 0, flex: 1 }} disabled>
                Uploading Files ({files.filter((f) => f.status === "uploading").length + files.filter((f) => f.status === "success").length}/{files.length})...
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
